import { ApiClient } from "@ucl/library";
import { PersonalAccountBeneficiary } from "@/types";

class PersonalAccountBeneficiaryApiClient {
  private _apiClient: ApiClient;

  constructor(apiClient: ApiClient) {
    this._apiClient = apiClient;
  }

  /**
   * Creates a new personal account beneficiary for a financial application.
   */
  createPersonalAccountBeneficiary = async (financialApplicationId: string) => {
    return this._apiClient.post<PersonalAccountBeneficiary>(
      `api/financial-application/${financialApplicationId}/personal-account-beneficiaries/`
    );
  };

  /**
   * Validates personal account beneficiaries for a financial application.
   */
  validatePersonalAccountBeneficiaries = async (financialApplicationId: string) => {
    return this._apiClient.post<Record<string, Record<string, string[]>>>(
      `api/financial-application/${financialApplicationId}/personal-account-beneficiaries/validate`
    );
  };

  /**
   * Retrieves a personal account beneficiary by their ID.
   */
  getPersonalAccountBeneficiary = async (financialApplicationId: string, beneficiaryId: string) => {
    return this._apiClient.get<PersonalAccountBeneficiary>(
      `api/financial-application/${financialApplicationId}/personal-account-beneficiaries/${beneficiaryId}`
    );
  };

  /**
   * Updates an existing personal account beneficiary for a financial application.
   */
  updatePersonalAccountBeneficiary = async (
    financialApplicationId: string,
    beneficiaryId: string,
    data: PersonalAccountBeneficiary
  ) => {
    return this._apiClient.put<PersonalAccountBeneficiary>(
      `api/financial-application/${financialApplicationId}/personal-account-beneficiaries/${beneficiaryId}`,
      data
    );
  };

  /**
   * Deletes a personal account beneficiary from a financial application.
   */
  deletePersonalAccountBeneficiary = async (financialApplicationId: string, beneficiaryId: string) => {
    return this._apiClient.delete<PersonalAccountBeneficiary>(
      `api/financial-application/${financialApplicationId}/personal-account-beneficiaries/${beneficiaryId}`
    );
  };

  /**
   * Retrieves a list of personal account beneficiary IDs for a financial application.
   */
  getPersonalAccountBeneficiaryIds = async (financialApplicationId: string) => {
    return this._apiClient.get<string[]>(
      `api/financial-application/${financialApplicationId}/personal-account-beneficiaries/ids`
    );
  };

  /**
   * Retrieves all personal account beneficiaries for a financial application.
   */
  getPersonalAccountBeneficiaries = async (financialApplicationId: string) => {
    return this._apiClient.get<PersonalAccountBeneficiary[]>(
      `api/financial-application/${financialApplicationId}/personal-account-beneficiaries`
    );
  };

  /**
   * Creates or updates a bequest for a personal account beneficiary on a specific deposit application.
   */
  createOrUpdateBequest = async (
    financialApplicationId: string,
    beneficiaryId: string,
    depositApplicationId: string,
    percentage: number
  ) => {
    return this._apiClient.put(
      `api/financial-application/${financialApplicationId}/personal-account-beneficiaries/${beneficiaryId}/deposit-applications/${depositApplicationId}/bequest`,
      { percentageBequeathed: percentage }
    );
  };

  /**
   * Removes a bequest for a personal account beneficiary from a specific deposit application.
   */
  removeBequest = async (
    financialApplicationId: string,
    beneficiaryId: string,
    depositApplicationId: string
  ) => {
    return this._apiClient.delete(
      `api/financial-application/${financialApplicationId}/personal-account-beneficiaries/${beneficiaryId}/deposit-applications/${depositApplicationId}/bequest`
    );
  };
}

export default PersonalAccountBeneficiaryApiClient; 